import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { BusinessType } from "@/types/payment";
import { Building, Building2, Users } from "lucide-react";

// Supported industries in the application
const supportedIndustries = [
  { value: "ecommerce", label: "E-commerce" },
  { value: "finance", label: "Finance" },
  { value: "logistics", label: "Logistics" },
  { value: "tech", label: "Technology" },
  { value: "realestate", label: "Real Estate" }
];

const businessTypeInfo = {
  small: {
    title: "Small Business (Startup)",
    icon: Building,
    description: "Perfect for startups and small teams (1-10 employees)",
    setupFee: 750,
    flutterwaveLink: "https://flutterwave.com/pay/8zbx459jbzbb",
  },
  medium: {
    title: "Mid-Sized Business", 
    icon: Building2,
    description: "Ideal for growing companies (11-100 employees)",
    setupFee: 1500,
    flutterwaveLink: "https://flutterwave.com/pay/z0ubrblz7uif",
  },
  enterprise: {
    title: "Enterprise",
    icon: Users,
    description: "For large organizations (100+ employees)",
    setupFee: 2500,
    flutterwaveLink: "https://flutterwave.com/pay/qv83cxn44krr",
  },
};

const CompanyRegistration = () => {
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [employeeCount, setEmployeeCount] = useState("");
  const [businessSize, setBusinessSize] = useState<BusinessType>("small");
  const [industry, setIndustry] = useState<string>("");
  const [authLoading, setAuthLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth", { replace: true });
        return;
      }
      
      // Check if user already completed onboarding
      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();
      
      if (profile) {
        // User already has a profile, redirect to dashboard
        navigate("/", { replace: true });
        return;
      }
    } catch (error) {
      console.error("Auth check failed:", error);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        throw new Error("No authenticated session found. Please try logging in again.");
      }

      const user = session.user;

      // Create profile with company details
      const { error: profileError } = await supabase
        .from("profiles")
        .insert({
          id: user.id,
          company_name: companyName,
          employee_count: parseInt(employeeCount),
          business_size: businessSize,
          industry: industry
        });

      if (profileError) throw profileError;

      // Create a payment record for tracking
      const businessInfo = businessTypeInfo[businessSize];
      const { error: paymentError } = await supabase
        .from('payments')
        .insert({
          user_id: user.id,
          transaction_ref: `setup_${businessSize}_${Date.now()}`,
          amount: businessInfo.setupFee,
          currency: "USD",
          payment_type: "one_time",
          status: "pending",
          business_type: businessSize,
          metadata: {
            setup_type: "business_setup",
            business_size: businessSize,
            company_name: companyName,
            employee_count: parseInt(employeeCount),
            industry: industry,
            flutterwave_link: businessInfo.flutterwaveLink,
          },
        });

      if (paymentError) {
        console.error("Error creating payment record:", paymentError);
        toast.error("Failed to initialize payment. Please try again.");
        return;
      }

      toast.success("Company details saved! Redirecting to payment...");
      
      // Redirect to Flutterwave payment link
      setTimeout(() => {
        window.location.href = businessInfo.flutterwaveLink;
      }, 1000);

    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        <Card className="p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold">Complete Your Registration</h2>
            <p className="text-gray-600 mt-2">Tell us about your company to get started</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">Company Name</label>
              <Input
                placeholder="Your Company Name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Number of Employees</label>
              <Input
                type="number"
                placeholder="e.g., 25"
                value={employeeCount}
                onChange={(e) => setEmployeeCount(e.target.value)}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Business Size</label>
              <Select
                value={businessSize}
                onValueChange={(value) => setBusinessSize(value as BusinessType)}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Business Size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small Business (1-10 employees)</SelectItem>
                  <SelectItem value="medium">Mid-Sized Business (11-100 employees)</SelectItem>
                  <SelectItem value="enterprise">Enterprise (100+ employees)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Industry</label>
              <Select
                value={industry}
                onValueChange={(value) => setIndustry(value)}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Your Industry" />
                </SelectTrigger>
                <SelectContent>
                  {supportedIndustries.map((ind) => (
                    <SelectItem key={ind.value} value={ind.value}>
                      {ind.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {businessSize && (
              <div className="bg-blue-50 p-6 rounded-lg border">
                <div className="flex items-center space-x-4">
                  {(() => {
                    const Icon = businessTypeInfo[businessSize].icon;
                    return <Icon className="h-8 w-8 text-blue-600" />;
                  })()}
                  <div className="flex-1">
                    <h3 className="font-semibold text-blue-900">
                      {businessTypeInfo[businessSize].title}
                    </h3>
                    <p className="text-blue-700 text-sm">
                      {businessTypeInfo[businessSize].description}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-900">
                      ${businessTypeInfo[businessSize].setupFee.toLocaleString()}
                    </div>
                    <div className="text-sm text-blue-600">Setup Fee</div>
                    <div className="text-xs text-green-600 font-medium">
                      + 7-day free trial
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <Button type="submit" className="w-full" size="lg" disabled={loading}>
              {loading ? "Processing..." : "Continue to Payment"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default CompanyRegistration;